import { useMemo } from 'react';
import type { SubAccountColumnParams } from '../components';
import { getAgGridColId } from '../components/BlotterTable/columns/getAgGridColId';
import type { PriceParams } from '../components/BlotterTable/columns/price.types';
import type { SizeColumnParams } from '../components/BlotterTable/columns/size.types';
import type { Column, ColumnDef } from '../components/BlotterTable/columns/types';
import { useDefaultColumns } from '../components/BlotterTable/useDefaultColumns';
import type { ExpectTrue } from '../tests';
import { EMPTY_ARRAY, type Leaves } from '../utils';
import { type AllocationBE, getEntitySubAccountNames } from './Allocation';
import type {
  AssetTransactionActionEnum,
  AssetTransactionAllocationStatusEnum,
  AssetTransactionPriceSourceEnum,
  AssetTransactionSettlementStatusEnum,
  AssetTransactionSourceTypeEnum,
  AssetTransactionTypeEnum,
  IAssetTransaction,
} from './types';

export function getAssetTransactionRowID(tid: string, marketAccount: string) {
  return `${marketAccount}-${tid}`;
}

export class AssetTransaction {
  static readonly StreamName = 'AssetTransaction';
  static readonly BlotterExportName = 'Cash Transactions';

  static readonly defaultColumns = [
    'TransactTime',
    'Type',
    'TID',
    'TransactionID',
    'Market',
    'MarketAccount',
    'PositionAsset',
    'Amount',
    'Asset',
    'Allocation',
    'AllocationStatus',
    'Comment',
    'UpdateUser',
  ] satisfies (keyof AssetTransaction)[];

  Timestamp!: string;
  Type!: AssetTransactionTypeEnum;
  TID!: string;
  Market!: string;
  CredentialID!: number;
  MarketAccount!: string;
  Asset!: string;
  Amount!: string;
  Rate!: string;
  PositionAsset!: string;
  TransactionID!: string;
  TransactTime!: string;
  OrgID!: number;
  SettleStatus!: AssetTransactionSettlementStatusEnum;
  SourceType!: AssetTransactionSourceTypeEnum;

  Price?: string;
  PriceCurrency?: string;
  PriceSource?: AssetTransactionPriceSourceEnum;
  TransactionAction?: AssetTransactionActionEnum;
  Comment?: string;
  TransactionDetails?: unknown;

  // AllocationStatus is always defined but setting as maybe undefined for backwards backend compat.
  AllocationStatus?: AssetTransactionAllocationStatusEnum;
  Allocation?: AllocationBE;
  SubAccount?: string;
  UpdateUser?: string;
  Revision?: number;

  /** An array of all the sub accounts used. For allocs, will be the alloc members. If no allocs, will just be the sub account in an array. Else undefined. */
  subAccounts: string[] | undefined;

  constructor(data: IAssetTransaction) {
    Object.assign(this, data);
    this.subAccounts = getEntitySubAccountNames(this);
  }

  get rowID() {
    return getAssetTransactionRowID(this.TID, this.MarketAccount);
  }
}

interface UseAssetTransactionColumns {
  defaultColumns?: (keyof AssetTransaction | Partial<Column>)[];
}

export function useAssetTransactionColumns({ defaultColumns = EMPTY_ARRAY }: UseAssetTransactionColumns): Column[] {
  const columnDefinitions = useMemo(
    () =>
      new Map(
        (
          [
            {
              field: 'TransactTime',
              type: 'date',
              initialWidth: 200,
              sortable: true,
            },
            {
              field: 'Type',
              type: 'text',
            },
            {
              field: 'TID',
              type: 'text',
            },
            {
              field: 'TransactionID',
              type: 'text',
            },
            {
              field: 'Market',
              type: 'market',
            },
            {
              field: 'MarketAccount',
              type: 'marketAccount',
            },
            {
              field: 'PositionAsset',
              type: 'asset',
            },
            {
              field: 'Amount',
              type: 'size',
              params: {
                currencyField: 'Asset',
                highlightNegative: true,
              } satisfies SizeColumnParams<AssetTransaction>,
            },
            {
              field: 'Asset',
              type: 'asset',
            },
            {
              field: 'Price',
              type: 'price',
              params: {
                assetField: 'Asset',
                quoteCurrencyField: 'PriceCurrency',
              } satisfies PriceParams<AssetTransaction>,
            },
            {
              field: 'PriceCurrency',
              type: 'currency',
            },
            {
              field: 'Rate',
              type: 'number',
            },
            {
              field: 'subAccounts',
              type: 'subAccount',
              id: 'Allocation' satisfies Leaves<AssetTransaction>,
              params: {
                allocationField: 'Allocation',
                allocationCurrencyField: 'Asset',
              } satisfies SubAccountColumnParams<AssetTransaction>,
            },
            {
              type: 'text',
              field: 'AllocationStatus',
            },
            {
              type: 'text',
              field: 'Comment',
              initialWidth: 200,
            },
            {
              type: 'text',
              field: 'UpdateUser',
            },
            {
              field: 'Revision',
              type: 'text',
              params: {
                getLabel: (value: number | null | undefined) => {
                  if (value == null || value === 0) {
                    return '';
                  }
                  return String(value);
                },
              },
            },
          ] satisfies ColumnDef<AssetTransaction>[]
        ).map(c => [getAgGridColId(c), c])
      ),
    []
  );

  return useDefaultColumns(defaultColumns, columnDefinitions);
}

// TYPE LEVEL TESTS
type _Expect_AssetTransaction_To_Only_Have_Keys_From_IAssetTransaction = ExpectTrue<
  {
    [K in keyof AssetTransaction & string]: K extends keyof IAssetTransaction ? true : K;
  }[Exclude<
    keyof AssetTransaction,
    'Allocation' | 'AllocationStatus' | 'SubAccount' | 'UpdateUser' | 'rowID' | 'subAccounts'
  >]
>;
type _Expect_All_Keys_In_IAssetTransaction_To_Be_In_AssetTransaction = ExpectTrue<
  {
    [K in keyof IAssetTransaction & string]: K extends keyof AssetTransaction ? true : K;
  }[Exclude<keyof IAssetTransaction, never>]
>;
